import { motion } from "framer-motion";
import { useEffect, useState } from "react";

const IMG_1 =
  "https://images.unsplash.com/flagged/photo-1569948807383-bc995c4f6dd1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxlbWVyZ2VuY3klMjBtZWRpY2FsJTIwcmVzcG9uc2UlMjBhY3Rpb258ZW58MHx8fHwxNzc4NTAwNjAxfDA&ixlib=rb-4.1.0&q=85";
const IMG_2 =
  "https://images.unsplash.com/photo-1634025130850-1d24389e25c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxlbWVyZ2VuY3klMjBtZWRpY2FsJTIwcmVzcG9uc2UlMjBhY3Rpb258ZW58MHx8fHwxNzc4NTAwNjAxfDA&ixlib=rb-4.1.0&q=85";

const stats = [
  { label: "Cardiac arrest survival window", value: "4", unit: "MIN" },
  { label: "Severe burns optimal care window", value: "1", unit: "HR" },
  { label: "Trauma golden hour interventions", value: "60", unit: "MIN" },
];

const Counter = ({ to }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const dur = 1500;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setN(Math.floor(p * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to]);
  return <>{n}</>;
};

export const EverySecond = () => {
  return (
    <section
      id="every-second"
      data-testid="every-second-section"
      className="relative section-pad bg-[#0A0A0C] overflow-hidden"
    >
      <div className="absolute inset-x-0 top-0 divider-grad" />
      <div className="absolute -top-10 -left-40 w-[700px] h-[700px] bg-radial-red opacity-70 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -right-40 w-[500px] h-[500px] bg-radial-red opacity-50 blur-3xl pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-center gap-3 mb-5">
          <span className="dot-pulse" />
          <span className="eyebrow">EVERY SECOND MATTERS · 006</span>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-8 font-display text-5xl sm:text-7xl lg:text-[96px] leading-[0.95] tracking-tighter font-medium text-white"
          >
            Every second
            <br />
            <span className="bg-gradient-to-r from-[#FF2400] to-[#FF6A4D] bg-clip-text text-transparent">
              is a life
            </span>{" "}
            in the balance.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-4 text-base sm:text-lg text-blude-textDim self-end leading-relaxed text-justify"
          >
            Behind every line of code, every formulation, every clinical trial -
            a person waiting. We build for the responder racing the clock, for
            the family praying, for the moment everything depends on what comes
            next.
          </motion.p>
        </div>

        {/* Storytelling collage */}
        <div className="grid lg:grid-cols-12 gap-5 mb-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="lg:col-span-7 relative h-[420px] rounded-2xl overflow-hidden border border-white/10 group"
          >
            <img
              src={IMG_1}
              alt="Emergency response"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.6s]"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0A0C] via-[#0A0A0C]/40 to-transparent" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(230,0,0,0.3),transparent_60%)]" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="eyebrow text-white/90">CASE · TRAUMA</div>
              <div className="mt-3 font-display text-3xl text-white tracking-tight max-w-lg">
                When minutes determine outcomes, formulations matter.
              </div>
            </div>
            <div className="absolute top-5 right-5 glass rounded-full px-3 py-1.5 flex items-center gap-2">
              <span className="dot-pulse" />
              <span className="font-mono text-[10px] tracking-widest text-white/80">
                LIVE FIELD
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.15 }}
            className="lg:col-span-5 relative h-[420px] rounded-2xl overflow-hidden border border-white/10 group"
          >
            <img
              src={IMG_2}
              alt="Emergency healthcare"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.6s]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/30 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="eyebrow text-white/90">CASE · BURNS</div>
              <div className="mt-3 font-display text-3xl text-white tracking-tight max-w-md">
                Healing begins with the first second of intervention.
              </div>
            </div>
          </motion.div>
        </div>

        {/* Metrics */}
        <div className="grid md:grid-cols-3 gap-5">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              className="card-tile p-7 relative"
              data-testid={`every-second-metric-${i + 1}`}
            >
              <div className="flex items-baseline gap-2 font-display tracking-tighter text-white">
                <span className="text-7xl">
                  <Counter to={parseInt(s.value)} />
                </span>
                <span className="text-2xl text-[#FF6A4D]">{s.unit}</span>
              </div>
              <div className="mt-3 text-sm text-blude-textDim leading-relaxed text-justify">
                {s.label}
              </div>
              <div className="mt-5 h-px bg-gradient-to-r from-[#E60000]/60 to-transparent" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
